/**
 * Which view the workspace opens in — Desktop, Browse or Search. Pure, so the
 * precedence is testable on its own.
 *
 * First match wins:
 *   1. an explicit `?view=desktop|browse|search`;
 *   2. a folder to reveal (`?folderPath=`) forces Browse, the view that can
 *      show it — a saved preference must never swallow a deep link;
 *   3. the person's saved choice (`fias.vault.view`, this browser only);
 *   4. Desktop.
 */
export const WORKSPACE_VIEWS = ['desktop', 'browse', 'search'] as const;
export type WorkspaceView = (typeof WORKSPACE_VIEWS)[number];

export const WORKSPACE_VIEW_STORAGE_KEY = 'fias.vault.view';

export function isWorkspaceView(value: unknown): value is WorkspaceView {
  return typeof value === 'string' && (WORKSPACE_VIEWS as readonly string[]).includes(value);
}

export function resolveWorkspaceView(input: {
  viewParam: string | null;
  folderPath: string | null;
  saved: string | null;
}): WorkspaceView {
  if (isWorkspaceView(input.viewParam)) return input.viewParam;
  if (input.folderPath) return 'browse';
  if (isWorkspaceView(input.saved)) return input.saved;
  return 'desktop';
}

/** The saved choice, or null. Storage can be missing or throw (private
 *  windows, blocked site data); the workspace renders fine without it. */
export function readSavedWorkspaceView(): string | null {
  try {
    return window.localStorage.getItem(WORKSPACE_VIEW_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

export function saveWorkspaceView(view: WorkspaceView): void {
  try {
    window.localStorage.setItem(WORKSPACE_VIEW_STORAGE_KEY, view);
  } catch {
    // A per-browser convenience only — nothing to recover.
  }
}
